import styled from '@emotion/styled';
import { css_background, css_if, css_medium_up, css_quick, css_text } from '~/emotion/theme';
import { useActiveSection, useSections } from '~/hooks/useSections';
import { MicrositeZIndex } from '~/global';
import { useState } from 'react';
import MicrositePortal from '~/components/MicrositePortal';

const MenuOverlay = styled.div<{ open: boolean }>`
	position: fixed;
	inset: 0;
	z-index: ${MicrositeZIndex.MENU};
	display: flex;
	pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
	background: ${({ open }) => (open ? 'rgba(255, 255, 255, 0.72)' : 'transparent')};
	transition: background 260ms ease;
`;

const MenuPanel = styled.div<{ open: boolean }>`
	position: relative;
	width: min(34rem, 100%);
	height: 100%;
	padding: 6rem 2rem 2rem;
	background: ${css_background()};
	transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(-100%)')};
	transition: transform 320ms cubic-bezier(0.77, 0, 0.175, 1);
	box-shadow: ${css_if('open', '24px 0 80px rgba(15, 23, 42, 0.18)', 'none')};
	@media (${css_medium_up}) {
		padding: 7rem 4rem 3rem;
	}
`;

const CloseButton = styled.button`
	position: absolute;
	top: 1.5rem;
	left: 1.5rem;
	width: 3rem;
	height: 3rem;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: none;
	font-size: 2rem;
	cursor: pointer;
`;

const MenuNav = styled.nav`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const MenuLink = styled.button<{ active: boolean }>`
	text-align: left;
	background: transparent;
	border: none;
	padding: 0;
	cursor: pointer;
	text-transform: uppercase;
	letter-spacing: 0.18em;
	font-size: clamp(1.35rem, 2vw, 2.25rem);
	color: ${({ active, theme }) => (active ? theme.color.black.text.strong : theme.color.black.text.weak)};
`;

const MenuButton = styled.button<{ open: boolean }>`
	display: inline-flex;
	flex-direction: column;
	gap: 0.35rem;

	span {
		width: 1.5rem;
		height: 2px;
		background: ${css_text()};
		transition:
			transform ${css_quick},
			opacity ${css_quick};
		transform-origin: center;
	}

	span:nth-of-type(1) {
		transform: ${css_if('open', 'translateY(0.45rem) rotate(45deg)', 'none')};
	}

	span:nth-of-type(2) {
		opacity: ${css_if('open', 0, 1)};
	}

	span:nth-of-type(3) {
		transform: ${css_if('open', 'translateY(-0.45rem) rotate(-45deg)', 'none')};
	}
`;

export default function MicrositeMenuBeta() {
	const sections = useSections();
	const active = useActiveSection();
	const [open, setOpen] = useState(false);

	return (
		<>
			<MenuButton type="button" open={open} onClick={() => setOpen(true)}>
				<span />
				<span />
				<span />
			</MenuButton>
			<MicrositePortal>
				<MenuOverlay open={open} onClick={() => setOpen(false)}>
					<MenuPanel open={open} onClick={(event) => event.stopPropagation()}>
						<CloseButton type="button" onClick={() => setOpen(false)}>
							×
						</CloseButton>
						<MenuNav>
							{sections.map((section) => (
								<MenuLink
									key={section.id}
									type="button"
									active={active === section.id}
									onClick={() => {
										section.navigate();

										setOpen(false);
									}}
								>
									{section.name}
								</MenuLink>
							))}
						</MenuNav>
					</MenuPanel>
				</MenuOverlay>
			</MicrositePortal>
		</>
	);
}
